import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Space,
  Typography,
  Tag,
  Tooltip,
  message,
  Popconfirm,
  Input,
  Row,
  Col,
  Statistic,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  FileTextOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import type { HRContractType } from "../../types/hr";
import { useHRAPI } from "../../hooks/useHRAPI";
import ContractTypeForm from "./ContractTypeForm";
import DefaultContractTypesHelper from "./DefaultContractTypesHelper";

const { Title } = Typography;
const { Search } = Input;

const ContractTypeList: React.FC = () => {
  const [contractTypes, setContractTypes] = useState<HRContractType[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [modalVisible, setModalVisible] = useState(false);
  const [editingContractType, setEditingContractType] =
    useState<HRContractType | null>(null);

  const { getContractTypes, deleteContractType } = useHRAPI();

  const fetchContractTypes = async () => {
    setLoading(true);
    try {
      const types = await getContractTypes();
      if (types) {
        setContractTypes(types);
      }
    } catch (error) {
      console.error("Erreur lors du chargement des types de contrats:", error);
      message.error("Erreur lors du chargement des types de contrats");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContractTypes();
  }, []);

  const handleCreateNew = () => {
    setEditingContractType(null);
    setModalVisible(true);
  };

  const handleEdit = (record: HRContractType) => {
    setEditingContractType(record);
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteContractType(id);
      message.success("Type de contrat supprimé avec succès");
      fetchContractTypes();
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
      message.error("Erreur lors de la suppression du type de contrat");
    }
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setEditingContractType(null);
  };

  const handleSuccess = () => {
    setModalVisible(false);
    setEditingContractType(null);
    fetchContractTypes();
  };

  // Filtrer les types de contrats selon la recherche
  const filteredContractTypes = contractTypes.filter(
    (type) =>
      type.name.toLowerCase().includes(searchText.toLowerCase()) ||
      (type.code &&
        type.code.toLowerCase().includes(searchText.toLowerCase())) ||
      (type.description &&
        type.description.toLowerCase().includes(searchText.toLowerCase()))
  );

  // Statistiques
  const stats = {
    total: contractTypes.length,
    active: contractTypes.filter((t) => t.is_active).length,
    renewable: contractTypes.filter((t) => t.renewable).length,
    withTrial: contractTypes.filter((t) => t.trial_period_days > 0).length,
  };

  const columns = [
    {
      title: "Nom",
      dataIndex: "name",
      key: "name",
      render: (text: string, record: HRContractType) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text}</div>
          {record.code && (
            <div style={{ fontSize: "12px", color: "#666" }}>
              Code: {record.code}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (text: string) => (
        <div style={{ maxWidth: 300 }}>
          {text ? (
            <Tooltip title={text}>
              <span>
                {text.length > 50 ? `${text.substring(0, 50)}...` : text}
              </span>
            </Tooltip>
          ) : (
            <span style={{ color: "#ccc" }}>Aucune description</span>
          )}
        </div>
      ),
    },
    {
      title: "Durée max",
      dataIndex: "max_duration_months",
      key: "max_duration_months",
      render: (months: number) => (months ? `${months} mois` : "Illimitée"),
    },
    {
      title: "Période d'essai",
      dataIndex: "trial_period_days",
      key: "trial_period_days",
      render: (days: number) => (days > 0 ? `${days} jours` : "Aucune"),
    },
    {
      title: "Préavis",
      dataIndex: "notice_period_days",
      key: "notice_period_days",
      render: (days: number) => `${days} jours`,
    },
    {
      title: "Renouvelable",
      dataIndex: "renewable",
      key: "renewable",
      render: (renewable: boolean) => (
        <Tag color={renewable ? "green" : "red"}>
          {renewable ? "Oui" : "Non"}
        </Tag>
      ),
    },
    {
      title: "Statut",
      dataIndex: "is_active",
      key: "is_active",
      render: (isActive: boolean) => (
        <Tag color={isActive ? "green" : "red"}>
          {isActive ? "Actif" : "Inactif"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: HRContractType) => (
        <Space>
          <Tooltip title="Modifier">
            <Button
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Supprimer">
            <Popconfirm
              title="Êtes-vous sûr de vouloir supprimer ce type de contrat ?"
              onConfirm={() => handleDelete(record.id)}
              okText="Oui"
              cancelText="Non"
            >
              <Button icon={<DeleteOutlined />} size="small" danger />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>
          <FileTextOutlined style={{ marginRight: 8 }} />
          Types de contrats
        </Title>
      </div>

      {/* Aide pour créer des types par défaut si aucun type n'existe */}
      {contractTypes.length === 0 && !loading && (
        <DefaultContractTypesHelper onSuccess={fetchContractTypes} />
      )}

      {/* Statistiques */}
      {contractTypes.length > 0 && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="Total"
                value={stats.total}
                valueStyle={{ color: "#1890ff" }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Actifs"
                value={stats.active}
                valueStyle={{ color: "#52c41a" }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Renouvelables"
                value={stats.renewable}
                valueStyle={{ color: "#722ed1" }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Avec période d'essai"
                value={stats.withTrial}
                valueStyle={{ color: "#fa541c" }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Barre d'outils */}
      <Card style={{ marginBottom: 16 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Search
              placeholder="Rechercher un type de contrat..."
              allowClear
              style={{ width: 300 }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              prefix={<SearchOutlined />}
            />
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreateNew}
            >
              Nouveau type de contrat
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Tableau */}
      <Card>
        <Table
          columns={columns}
          dataSource={filteredContractTypes}
          rowKey="id"
          loading={loading}
          pagination={{
            total: filteredContractTypes.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} sur ${total} types de contrats`,
          }}
        />
      </Card>

      {/* Modal de création/édition */}
      <ContractTypeForm
        visible={modalVisible}
        onCancel={handleModalClose}
        onSuccess={handleSuccess}
        contractType={editingContractType}
      />
    </div>
  );
};

export default ContractTypeList;
